import gt from 'lodash-es/gt';
import isUndefined from 'lodash-es/isUndefined';
import lt from 'lodash-es/lt';

import { ICmpFunc, ILoopFunc } from './interfaces';
import Queue from './Queue';

interface BSTreeNode<T> {
	element: T;
	leftCh: BSTreeNode<T> | null;
	parent: BSTreeNode<T> | null;
	rightCh: BSTreeNode<T> | null;
}

function defaultCompare<T>(a: T, b: T): number {
	return lt(a, b) ? -1 : gt(a, b) ? 1 : 0;
}

export class BinarySearchTree<T> {
	private root: BSTreeNode<T> | null;
	private compare: ICmpFunc<T>;
	private nElements: number;

	/**
	 * Creates an empty binary search tree.
	 */
	constructor(compareFunction?: ICmpFunc<T>) {
		this.root = null;
		this.compare = compareFunction || defaultCompare;
		this.nElements = 0;
	}

	/**
	 * Adds the specified element to this tree if it is not already present.
	 * @param {Object} element the element to insert.
	 * @return {boolean} true if this tree did not already contain the specified element.
	 */
	public add(element: T): boolean {
		if (isUndefined(element)) {
			return false;
		}

		if (this.insertNode(this.createNode(element)) !== null) {
			this.nElements++;
			return true;
		}
		return false;
	}

	/**
	 * Removes all of the elements from this tree.
	 */
	public clear(): void {
		this.root = null;
		this.nElements = 0;
	}

	/**
	 * Returns true if this tree contains no elements.
	 * @return {boolean} true if this tree contains no elements.
	 */
	public isEmpty(): boolean {
		return this.nElements === 0;
	}

	/**
	 * Returns the number of elements in this tree.
	 * @return {number} the number of elements in this tree.
	 */
	public size(): number {
		return this.nElements;
	}

	/**
	 * Returns true if this tree contains the specified element.
	 * @param {Object} element element to search for.
	 * @return {boolean} true if this tree contains the specified element,
	 * false otherwise.
	 */
	public contains(element: T): boolean {
		if (isUndefined(element)) {
			return false;
		}
		return this.searchNode(this.root, element) !== null;
	}

	/**
	 * Removes the specified element from this tree if it is present.
	 * @return {boolean} true if this tree contained the specified element.
	 */
	public remove(element: T): boolean {
		const node = this.searchNode(this.root, element);
		if (node === null) {
			return false;
		}
		this.removeNode(node);
		this.nElements--;
		return true;
	}

	/**
	 * Executes the provided function once for each element present in this tree in
	 * in-order.
	 * @param {function(Object):*} callback function to execute, it is invoked with one
	 * argument: the element value, to break the iteration you can optionally return false.
	 */
	public inorderTraversal(callback: ILoopFunc<T>): void {
		this.inorderTraversalAux(this.root, callback, {
			stop: false
		});
	}

	/**
	 * Executes the provided function once for each element present in this tree in pre-order.
	 * @param {function(Object):*} callback function to execute, it is invoked with one
	 * argument: the element value, to break the iteration you can optionally return false.
	 */
	public preorderTraversal(callback: ILoopFunc<T>): void {
		this.preorderTraversalAux(this.root, callback, {
			stop: false
		});
	}

	/**
	 * Executes the provided function once for each element present in this tree in post-order.
	 * @param {function(Object):*} callback function to execute, it is invoked with one
	 * argument: the element value, to break the iteration you can optionally return false.
	 */
	public postorderTraversal(callback: ILoopFunc<T>): void {
		this.postorderTraversalAux(this.root, callback, {
			stop: false
		});
	}

	/**
	 * Executes the provided function once for each element present in this tree in
	 * level-order.
	 * @param {function(Object):*} callback function to execute, it is invoked with one
	 * argument: the element value, to break the iteration you can optionally return false.
	 */
	public levelTraversal(callback: ILoopFunc<T>): void {
		this.levelTraversalAux(this.root, callback);
	}

	/**
	 * Returns the minimum element of this tree.
	 * @return {*} the minimum element of this tree or undefined if this tree is
	 * is empty.
	 */
	public minimum(): T | undefined {
		if (this.isEmpty()) {
			return undefined;
		}
		const minaux = this.minimumAux(this.root);
		return (minaux && minaux.element) || undefined;
	}

	/**
	 * Returns the maximum element of this tree.
	 * @return {*} the maximum element of this tree or undefined if this tree is
	 * is empty.
	 */
	public maximum(): T | undefined {
		if (this.isEmpty()) {
			return undefined;
		}
		const maxaux = this.maximumAux(this.root);
		return (maxaux && maxaux.element) || undefined;
	}

	/**
	 * Executes the provided function once for each element present in this tree in inorder.
	 * Equivalent to inorderTraversal.
	 * @param {function(Object):*} callback function to execute, it is
	 * invoked with one argument: the element value, to break the iteration you can
	 * optionally return false.
	 */
	public forEach(callback: ILoopFunc<T>): void {
		this.inorderTraversal(callback);
	}

	/**
	 * Returns an array containing all of the elements in this tree in in-order.
	 * @return {Array} an array containing all of the elements in this tree in in-order.
	 */
	public toArray(): T[] {
		const array: T[] = [];
		this.inorderTraversal((element: T): boolean => {
			array.push(element);
			return true;
		});
		return array;
	}

	/**
	 * Returns the height of this tree.
	 * @return {number} the height of this tree or -1 if is empty.
	 */
	public height(): number {
		return this.heightAux(this.root);
	}

	private searchNode(node: BSTreeNode<T> | null, element: T): BSTreeNode<T> | null {
		let cmp: number | null = null;
		while (node !== null && cmp !== 0) {
			cmp = this.compare(element, node.element);
			if (cmp < 0) {
				node = node.leftCh;
			} else if (cmp > 0) {
				node = node.rightCh;
			}
		}
		return node;
	}

	/**
	 * @private
	 */
	private transplant(n1: BSTreeNode<T> | null, n2: BSTreeNode<T> | null): void {
		if (n1 == null) {
			return;
		}
		if (n1.parent === null) {
			this.root = n2;
		} else if (n1 === n1.parent.leftCh) {
			n1.parent.leftCh = n2;
		} else {
			n1.parent.rightCh = n2;
		}
		if (n2 !== null) {
			n2.parent = n1.parent;
		}
	}

	/**
	 * @private
	 */
	private removeNode(node: BSTreeNode<T> | null): void {
		if (node == null) {
			return;
		}
		if (node.leftCh === null) {
			this.transplant(node, node.rightCh);
		} else if (node && node.rightCh === null) {
			this.transplant(node, node.leftCh);
		} else {
			const y = this.minimumAux(node.rightCh);
			if (y && y.parent !== node) {
				this.transplant(y, y.rightCh);
				if (node.rightCh) {
					y.rightCh = node.rightCh;
					y.rightCh.parent = y;
				}
			}
			this.transplant(node, y);
			if (y) {
				y.leftCh = node.leftCh;
				y.leftCh.parent = y;
			}
		}
	}

	private inorderTraversalAux(node: BSTreeNode<T> | null, callback: ILoopFunc<T>, signal: { stop: boolean }): void {
		if (node === null || signal.stop) {
			return;
		}
		this.inorderTraversalAux(node.leftCh, callback, signal);
		if (signal.stop) {
			return;
		}
		signal.stop = callback(node.element) === false;
		if (signal.stop) {
			return;
		}
		this.inorderTraversalAux(node.rightCh, callback, signal);
	}

	private levelTraversalAux(node: BSTreeNode<T> | null, callback: ILoopFunc<T>): void {
		const queue = new Queue<BSTreeNode<T>>();
		if (node !== null) {
			queue.enqueue(node);
		}
		while (!queue.isEmpty()) {
			node = queue.dequeue() || null;
			if (node && callback(node.element) === false) {
				return;
			}
			if (node && node.leftCh !== null) {
				queue.enqueue(node.leftCh);
			}
			if (node && node.rightCh !== null) {
				queue.enqueue(node.rightCh);
			}
		}
	}

	private preorderTraversalAux(node: BSTreeNode<T> | null, callback: ILoopFunc<T>, signal: { stop: boolean }) {
		if (node === null || signal.stop) {
			return;
		}
		signal.stop = callback(node.element) === false;
		if (signal.stop) {
			return;
		}
		this.preorderTraversalAux(node.leftCh, callback, signal);
		if (signal.stop) {
			return;
		}
		this.preorderTraversalAux(node.rightCh, callback, signal);
	}

	private postorderTraversalAux(node: BSTreeNode<T> | null, callback: ILoopFunc<T>, signal: { stop: boolean }) {
		if (node === null || signal.stop) {
			return;
		}
		this.postorderTraversalAux(node.leftCh, callback, signal);
		if (signal.stop) {
			return;
		}
		this.postorderTraversalAux(node.rightCh, callback, signal);
		if (signal.stop) {
			return;
		}
		signal.stop = callback(node.element) === false;
	}

	private minimumAux(node: BSTreeNode<T> | null): BSTreeNode<T> | null {
		while (node && node.leftCh !== null) {
			node = node.leftCh;
		}
		return node;
	}

	private maximumAux(node: BSTreeNode<T> | null): BSTreeNode<T> | null {
		while (node && node.rightCh !== null) {
			node = node.rightCh;
		}
		return node;
	}

	private heightAux(node: BSTreeNode<T> | null): number {
		if (node === null) {
			return -1;
		}
		return Math.max(this.heightAux(node.leftCh), this.heightAux(node.rightCh)) + 1;
	}

	private insertNode(node: BSTreeNode<T>): BSTreeNode<T> | null {
		let parent: any = null;
		let position = this.root;
		let cmp: number | null = null;
		while (position !== null) {
			cmp = this.compare(node.element, position.element);
			if (cmp === 0) {
				return null;
			} else if (cmp < 0) {
				parent = position;
				position = position.leftCh;
			} else {
				parent = position;
				position = position.rightCh;
			}
		}
		node.parent = parent;
		if (parent === null) {
			// tree is empty
			this.root = node;
		} else if (this.compare(node.element, parent.element) < 0) {
			parent.leftCh = node;
		} else {
			parent.rightCh = node;
		}
		return node;
	}

	private createNode(element: T): BSTreeNode<T> {
		return {
			element,
			leftCh: null,
			parent: null,
			rightCh: null
		};
	}
}

export default BinarySearchTree;
